import { useEffect, useMemo, useState } from 'react';

import { DisposalRecordFloorUnit } from '@agency/apis/disposals';
import { PropertySpace } from '@apis/propertiesApi.types';
import { useAgencyConstants, useConfig } from '@core/contexts/ConfigContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Checkbox } from '@shared/components/checkbox';
import { Flex } from '@shared/components/flex';
import { SingleGroupSelect } from '@shared/components/group-select-new/single-group-select';
import { LocalSpaceLookup } from '@shared/components/local-space-lookup';
import { NumberField } from '@shared/components/number-field';
import { Option } from '@shared/components/select-new/Select';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { useCoreOptions } from '@shared/contexts/OptionsContext';
import { ButtonSize } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';
import { groupOptionsByBuildingType } from '@shared/utils/options';

import { styles } from './RecordSpaceItem.styles';
import { RecordSpaceItemError } from '../../RecordSpacesTakenForm';

export type EditCardUpdate = Partial<DisposalRecordFloorUnit>;

export type EditCard = (update: EditCardUpdate) => void;

export interface RecordSpaceItemInterface extends WithStyles<typeof styles> {
  card: DisposalRecordFloorUnit;
  disabled?: boolean;
  entireProperty?: number;
  errorValidation?: RecordSpaceItemError;
  fitoutConditionOptions?: Option[];
  propertyId?: Id;
  editCard: EditCard;
  onSizeMetricChange: (id: Metric) => void;
}

const RecordSpaceItemComponents: React.FC<RecordSpaceItemInterface> = ({
  card,
  classes,
  disabled = false,
  entireProperty,
  errorValidation,
  propertyId,
  editCard,
  onSizeMetricChange,
}) => {
  const { constants } = useAgencyConstants();
  const { user } = useConfig();
  const { floorUnitNameOptions } = useCoreOptions();

  const [error, setError] = useState<RecordSpaceItemError | undefined>(undefined);

  useEffect(() => {
    setError(errorValidation);
  }, [errorValidation]);

  const buildingTypeGroupOptions = useMemo(() => groupOptionsByBuildingType(constants, user), [constants, user]);

  const changeFloor = (value: Id) => {
    const update: EditCardUpdate = {
      floor: value.toString(),
      size_value: '',
      size_metric: constants.defaultSizeMetric || '',
    };
    if (value === 'unit') {
      update.part_floor = false;
    }
    editCard(update);
  };

  const handleSelectSpace = (name: string, space?: PropertySpace) => {
    if (space) {
      const update: EditCardUpdate = {
        size_metric: space.size_metric || '',
        size_value: space.size_value || '',
        unit: space.floor_description,
        unit_string: name,
      };

      editCard(update);
    } else {
      const update: EditCardUpdate = {
        size_metric: constants.defaultSizeMetric || '',
        size_value: '',
        unit: null,
        unit_string: name,
      };

      editCard(update);
    }
  };

  const sizeSelectList = React.useMemo(() => {
    if (constants.availableSizeMetrics) {
      const sizeMetrics = constants.availableSizeMetrics;

      return Object.keys(sizeMetrics).map((el) => {
        return { label: sizeMetrics[el], id: el } as Option<Metric>;
      });
    }
    return [];
  }, [constants]);

  if (card.id) {
    return (
      <div className={classes.wrap}>
        <Checkbox
          classes={{ rootIconOnly: classes.includeCheckbox }}
          checked={card.include === 1}
          disabled={disabled || entireProperty === 1}
          onChange={(checked) => editCard({ include: checked ? 1 : 0 })}
        />
        <span className={classes.spaceName}>
          {`${card.floor_string}${card.unit ? ` (${card.unit})` : ''} - `} <i>{card.status}</i>
        </span>
        <Flex wrap="nowrap" classes={{ root: classes.sizeWrapper }}>
          <NumberField
            classes={{ root: classes.sizeField }}
            error={!!error?.size}
            size={InputSize.medium}
            placeholder={t('enter_size_1')}
            value={card.size_value}
            onChange={(v) => editCard({ size_value: v })}
            min={0}
          />
          <SingleSelect
            classes={{ root: classes.metricSelect }}
            size={ButtonSize.medium}
            options={sizeSelectList}
            value={card.size_metric}
            onChange={onSizeMetricChange}
          />
          <SingleGroupSelect
            classes={{ root: classes.propertyTypeSelect }}
            placeholder={t('xtextx_ellipsis', { text: t('property_types') })}
            size={ButtonSize.medium}
            value={card.property_types?.[0] || ''}
            groupOptions={buildingTypeGroupOptions}
            onChange={(value) => editCard({ property_types: value ? [value as string] : [] })}
          />
        </Flex>
      </div>
    );
  }

  return (
    <div className={classes.wrap}>
      <Checkbox
        classes={{ rootIconOnly: classes.includeCheckbox }}
        checked={card.include === 1}
        disabled={entireProperty === 1}
        onChange={(checked) => editCard({ include: checked ? 1 : 0 })}
      />
      <Flex autoWidth={false} direction="column" classes={{ root: classes.selectFloorContainer }}>
        <SingleSelect
          error={!!error?.floor}
          options={floorUnitNameOptions}
          placeholder={t('xtextx_ellipsis', { text: t('floor_or_unit') })}
          size={ButtonSize.medium}
          value={card.floor}
          onChange={changeFloor}
        />
      </Flex>
      <Checkbox
        checked={card.part_floor}
        label={t('part_question')}
        onChange={(checked: boolean) => editCard({ part_floor: checked })}
        classes={{ root: classes.floorCheckbox }}
      />
      <LocalSpaceLookup
        classes={{
          root: classes.description,
          label: classes.labelText,
        }}
        disabled={!card.floor}
        error={!!error?.description}
        floor={card.floor}
        unit={card.unit ? (card.unit as Id) : undefined}
        unit_string={card.unit as string}
        placeholder={t('xtextx_ellipsis', { text: t('description') })}
        propertyId={propertyId}
        setSpace={handleSelectSpace}
      />
      <Flex wrap="nowrap" classes={{ root: classes.sizeWrapper }}>
        <NumberField
          classes={{ root: classes.sizeField }}
          placeholder={t('enter_size_1')}
          error={!!error?.size}
          size={InputSize.medium}
          value={card.size_value}
          onChange={(v) => editCard({ size_value: v })}
          min={0}
        />
        <SingleSelect
          classes={{ root: classes.metricSelect }}
          size={ButtonSize.medium}
          options={sizeSelectList}
          value={card.size_metric}
          onChange={onSizeMetricChange}
        />
        <SingleGroupSelect
          classes={{ root: classes.propertyTypeSelect }}
          placeholder={t('xtextx_ellipsis', { text: t('property_types') })}
          size={ButtonSize.medium}
          value={card.property_types?.[0] || ''}
          groupOptions={buildingTypeGroupOptions}
          onChange={(value) => editCard({ property_types: value ? [value as string] : [] })}
        />
      </Flex>
    </div>
  );
};

export const RecordSpaceItem = withStyles(styles)(RecordSpaceItemComponents);
